// Hand-maintained — override generation
// Hand-maintained — safe to edit directly. See lib/gpt_core/sdk/CLAUDE.md for context.
// mix update.sdks preserves this file (only overwrites empty stubs). Do NOT add `return {};`.

import type {
  BrowserSession,
  CrawlerJob,
  CrawlerResult,
  CrawlerSchedule,
  CrawlerSiteConfig,
  NewsMonitor,
  NewsSummary,
} from "../_internal/types.gen";
import {
  getAdminCrawlerJobs,
  getAdminCrawlerJobsById,
  postAdminCrawlerJobs,
  patchAdminCrawlerJobsByIdCancel,
  deleteAdminCrawlerJobsById,
  getAdminCrawlerSchedules,
  getAdminCrawlerSchedulesById,
  postAdminCrawlerSchedules,
  patchAdminCrawlerSchedulesById,
  patchAdminCrawlerSchedulesByIdEnable,
  patchAdminCrawlerSchedulesByIdDisable,
  patchAdminCrawlerSchedulesByIdTrigger,
  deleteAdminCrawlerSchedulesById,
  getAdminCrawlerResults,
  getAdminCrawlerResultsById,
  getAdminCrawlerSiteConfigs,
  getAdminCrawlerSiteConfigsById,
  postAdminCrawlerSiteConfigs,
  patchAdminCrawlerSiteConfigsById,
  deleteAdminCrawlerSiteConfigsById,
  getAdminCrawlerNewsMonitors,
  getAdminCrawlerNewsMonitorsById,
  postAdminCrawlerNewsMonitors,
  patchAdminCrawlerNewsMonitorsById,
  patchAdminCrawlerNewsMonitorsByIdPause,
  patchAdminCrawlerNewsMonitorsByIdResume,
  patchAdminCrawlerNewsMonitorsByIdArchive,
  deleteAdminCrawlerNewsMonitorsById,
  getAdminCrawlerNewsSummaries,
  getAdminCrawlerNewsSummariesById,
  getAdminCrawlerBrowserSessions,
  getAdminCrawlerBrowserSessionsById,
  postAdminCrawlerBrowserSessions,
  patchAdminCrawlerBrowserSessionsByIdRefresh,
  deleteAdminCrawlerBrowserSessionsById,
} from "../_internal/sdk.gen";
import type { RequestOptions } from "../base-client";
import { buildPageQuery } from "../namespace-types";
import { RequestBuilder } from "../request-builder";

/**
 * Attributes accepted when creating a NewsMonitor via the admin SDK (B11
 * `:news_monitoring` capability). Typed explicitly — passing extra
 * properties is rejected server-side (additionalProperties: false).
 *
 * Use `frequency: "daily"` only — `"hourly"` and `"twice_daily"`
 * are tier-gated by `ValidateMonitorFrequencyAllowed` and rejected on
 * standard-tier applications until the premium frequency capability ships.
 */
export type CreateNewsMonitorAttributes = {
  name: string;
  topic: string;
  source_urls: string[];
  frequency?: "daily" | "twice_daily" | "hourly";
  summary_model?: "default" | "balanced" | "accurate";
  tags?: string[];
  metadata?: Record<string, unknown>;
};

/**
 * Attributes accepted when updating a NewsMonitor (PATCH semantics).
 *
 * Same tier-gating note as create: pass `frequency: "daily"` only —
 * `"hourly"` and `"twice_daily"` are gated by
 * `ValidateMonitorFrequencyAllowed` and rejected on standard-tier
 * applications until the premium frequency capability ships.
 */
export type UpdateNewsMonitorAttributes = {
  name?: string;
  topic?: string;
  source_urls?: string[];
  frequency?: "daily" | "twice_daily" | "hourly";
  summary_model?: "default" | "balanced" | "accurate";
  tags?: string[];
  metadata?: Record<string, unknown>;
};

function buildCreateNewsMonitorAttributes(
  attributes: CreateNewsMonitorAttributes,
) {
  const { name, topic, source_urls, frequency, summary_model, tags, metadata } =
    attributes;

  return { name, topic, source_urls, frequency, summary_model, tags, metadata };
}

function buildUpdateNewsMonitorAttributes(
  attributes: UpdateNewsMonitorAttributes,
) {
  const { name, topic, source_urls, frequency, summary_model, tags, metadata } =
    attributes;

  return { name, topic, source_urls, frequency, summary_model, tags, metadata };
}

/**
 * Attributes accepted when creating a crawl job via the admin SDK.
 * Mirrors `crawl_job.ex :create` accept list — passing extra properties
 * is rejected server-side (additionalProperties: false).
 */
export type AdminCreateCrawlerJobAttributes = {
  // TODO(F-33): workspace_id derivation decision deferred.
  workspace_id: string;
  url: string;
  mode?: "single" | "site" | "sitemap";
  strategy?: "native" | "sidecar" | "auto";
  depth?: number;
  max_pages?: number;
  include_patterns?: string[];
  exclude_patterns?: string[];
  output_format?: "markdown" | "html" | "text" | "snapshot";
  callback_url?: string;
  schedule_id?: string;
  metadata?: Record<string, unknown>;
  browser_steps?: Array<Record<string, unknown>>;
};

/**
 * Attributes accepted when creating a crawl schedule via the admin SDK.
 * Mirrors `crawl_schedule.ex :create` accept list.
 */
export type AdminCreateCrawlerScheduleAttributes = {
  // TODO(F-33): workspace_id derivation decision deferred.
  workspace_id: string;
  name: string;
  url: string;
  mode?: "single" | "site" | "sitemap";
  frequency?: "hourly" | "daily" | "weekly" | "monthly";
  // TODO(F-39): cron_expression to be dropped — accepted but never read.
  cron_expression?: string;
  crawl_config?: Record<string, unknown>;
  notify_on_change?: boolean;
  next_run_at?: string;
  news_monitor_id?: string;
};

/**
 * Attributes accepted when updating a crawl schedule via the admin SDK.
 * Mirrors `crawl_schedule.ex :update` accept list. To pause/resume use
 * `enable()` / `disable()` — `enabled` is not part of the update accept.
 */
export type AdminUpdateCrawlerScheduleAttributes = {
  name?: string;
  url?: string;
  mode?: "single" | "site" | "sitemap";
  frequency?: "hourly" | "daily" | "weekly" | "monthly";
  // TODO(F-39): cron_expression to be dropped — accepted but never read.
  cron_expression?: string;
  crawl_config?: Record<string, unknown>;
  notify_on_change?: boolean;
  news_monitor_id?: string;
};

/**
 * Attributes accepted when creating a SiteConfig via the admin SDK.
 * Mirrors `site_config.ex :create` accept list. Includes the auth-aware
 * `browser_auth_session_id` + `browser_fallback_strategy` fields.
 */
export type AdminCreateSiteConfigAttributes = {
  // TODO(F-33): workspace_id derivation decision deferred.
  workspace_id: string;
  domain: string;
  rate_limit_rpm?: number;
  preferred_strategy?: "native" | "sidecar" | "auto" | "browser_session";
  requires_js?: boolean;
  custom_headers?: Record<string, string>;
  user_agent?: string;
  respect_robots?: boolean;
  browser_auth_session_id?: string;
  browser_fallback_strategy?: "native" | "sidecar";
};

/**
 * Attributes accepted when updating a SiteConfig via the admin SDK.
 * Mirrors `site_config.ex :update` accept list.
 */
export type AdminUpdateSiteConfigAttributes = {
  rate_limit_rpm?: number;
  preferred_strategy?: "native" | "sidecar" | "auto" | "browser_session";
  requires_js?: boolean;
  custom_headers?: Record<string, string>;
  user_agent?: string;
  respect_robots?: boolean;
  browser_auth_session_id?: string;
  browser_fallback_strategy?: "native" | "sidecar";
};

/**
 * Attributes accepted when creating a BrowserSession via the admin SDK.
 * Mirrors `browser_session.ex :create` accept list. `session_state` is
 * encrypted at rest via `EncryptSessionState` change.
 */
export type AdminCreateBrowserSessionAttributes = {
  // TODO(F-33): workspace_id derivation decision deferred.
  workspace_id: string;
  domain: string;
  session_state: string;
  expires_at?: string;
};

/**
 * Attributes accepted when refreshing a BrowserSession. Mirrors
 * `browser_session.ex :refresh` accept list — only `session_state` may
 * be rotated; the refresh action re-encrypts and stamps `last_used_at`.
 */
export type AdminRefreshBrowserSessionAttributes = {
  session_state: string;
};

function buildAdminCreateCrawlerJobAttributes(
  attributes: AdminCreateCrawlerJobAttributes,
) {
  const {
    workspace_id,
    url,
    mode,
    strategy,
    depth,
    max_pages,
    include_patterns,
    exclude_patterns,
    output_format,
    callback_url,
    schedule_id,
    metadata,
    browser_steps,
  } = attributes;

  return {
    workspace_id,
    url,
    mode,
    strategy,
    depth,
    max_pages,
    include_patterns,
    exclude_patterns,
    output_format,
    callback_url,
    schedule_id,
    metadata,
    browser_steps,
  };
}

function buildAdminCreateCrawlerScheduleAttributes(
  attributes: AdminCreateCrawlerScheduleAttributes,
) {
  const {
    workspace_id,
    name,
    url,
    mode,
    frequency,
    cron_expression,
    crawl_config,
    notify_on_change,
    next_run_at,
    news_monitor_id,
  } = attributes;

  return {
    workspace_id,
    name,
    url,
    mode,
    frequency,
    cron_expression,
    crawl_config,
    notify_on_change,
    next_run_at,
    news_monitor_id,
  };
}

function buildAdminUpdateCrawlerScheduleAttributes(
  attributes: AdminUpdateCrawlerScheduleAttributes,
) {
  const {
    name,
    url,
    mode,
    frequency,
    cron_expression,
    crawl_config,
    notify_on_change,
    news_monitor_id,
  } = attributes;

  return {
    name,
    url,
    mode,
    frequency,
    cron_expression,
    crawl_config,
    notify_on_change,
    news_monitor_id,
  };
}

function buildAdminCreateSiteConfigAttributes(
  attributes: AdminCreateSiteConfigAttributes,
) {
  const {
    workspace_id,
    domain,
    rate_limit_rpm,
    preferred_strategy,
    requires_js,
    custom_headers,
    user_agent,
    respect_robots,
    browser_auth_session_id,
    browser_fallback_strategy,
  } = attributes;

  return {
    workspace_id,
    domain,
    rate_limit_rpm,
    preferred_strategy,
    requires_js,
    custom_headers,
    user_agent,
    respect_robots,
    browser_auth_session_id,
    browser_fallback_strategy,
  };
}

function buildAdminUpdateSiteConfigAttributes(
  attributes: AdminUpdateSiteConfigAttributes,
) {
  const {
    rate_limit_rpm,
    preferred_strategy,
    requires_js,
    custom_headers,
    user_agent,
    respect_robots,
    browser_auth_session_id,
    browser_fallback_strategy,
  } = attributes;

  return {
    rate_limit_rpm,
    preferred_strategy,
    requires_js,
    custom_headers,
    user_agent,
    respect_robots,
    browser_auth_session_id,
    browser_fallback_strategy,
  };
}

function buildAdminCreateBrowserSessionAttributes(
  attributes: AdminCreateBrowserSessionAttributes,
) {
  const { workspace_id, domain, session_state, expires_at } = attributes;

  return { workspace_id, domain, session_state, expires_at };
}

function buildAdminRefreshBrowserSessionAttributes(
  attributes: AdminRefreshBrowserSessionAttributes,
) {
  const { session_state } = attributes;

  return { session_state };
}

/**
 * Admin crawler namespace — full crawl lifecycle management including site configs.
 *
 * Extends the client crawler namespace with SiteConfig CRUD (per-domain rate limits,
 * strategy preferences, custom headers) which is admin-only.
 *
 * @example
 * ```typescript
 * const admin = new GptAdmin({ apiKey: 'sk_srv_...' });
 *
 * // List all crawl jobs
 * const jobs = await admin.crawler.jobs.list();
 *
 * // Configure per-domain crawl settings
 * const config = await admin.crawler.siteConfigs.create({
 *   workspace_id: 'ws_abc',
 *   domain: 'docs.example.com',
 *   rate_limit_rpm: 30,
 * });
 * ```
 */
export function createCrawlerNamespace(rb: RequestBuilder) {
  return {
    /** Crawl job management — create, cancel, delete jobs. */
    jobs: {
      /**
       * List crawl jobs across all workspaces visible to the admin actor.
       *
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to an array of {@link CrawlerJob} records.
       *
       * @example
       * ```typescript
       * const jobs = await admin.crawler.jobs.list();
       * console.log(`${jobs.length} jobs across all workspaces`);
       * ```
       */
      list: async (options?: RequestOptions): Promise<CrawlerJob[]> => {
        return rb.execute<CrawlerJob[]>(getAdminCrawlerJobs, {}, options);
      },

      /**
       * Fetch a single crawl job by id.
       *
       * @param id - The crawl job id.
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to the matching {@link CrawlerJob}.
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<CrawlerJob> => {
        return rb.execute<CrawlerJob>(
          getAdminCrawlerJobsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Start a new crawl job (admin context — may target any workspace).
       *
       * @param attributes - Crawl job attributes; must include `workspace_id`
       *   and `url`. Extra properties are dropped by the destructure helper
       *   before they reach the server.
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to the newly created {@link CrawlerJob}.
       *
       * @example
       * ```typescript
       * const job = await admin.crawler.jobs.create({
       *   workspace_id: 'ws_abc',
       *   url: 'https://docs.example.com',
       *   mode: 'site',
       *   depth: 2,
       * });
       * ```
       */
      create: async (
        attributes: AdminCreateCrawlerJobAttributes,
        options?: RequestOptions,
      ): Promise<CrawlerJob> => {
        const bodyAttributes = buildAdminCreateCrawlerJobAttributes(attributes);

        return rb.execute<CrawlerJob>(
          postAdminCrawlerJobs,
          {
            body: {
              data: { type: "crawler-job", attributes: bodyAttributes },
            },
          },
          options,
        );
      },

      /**
       * Cancel an in-progress crawl job. Sends a PATCH with no attributes.
       *
       * @param id - The crawl job id.
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to the updated {@link CrawlerJob}
       *   with `status: 'cancelled'`.
       *
       * @example
       * ```typescript
       * await admin.crawler.jobs.cancel('cjob_abc');
       * ```
       */
      cancel: async (
        id: string,
        options?: RequestOptions,
      ): Promise<CrawlerJob> => {
        return rb.execute<CrawlerJob>(
          patchAdminCrawlerJobsByIdCancel,
          { path: { id }, body: { data: { id, type: "crawler-job" } } },
          options,
        );
      },

      /**
       * Permanently delete a crawl job and its associated results.
       *
       * @param id - The crawl job id.
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to `true` on successful deletion.
       *
       * @example
       * ```typescript
       * await admin.crawler.jobs.delete('cjob_abc');
       * ```
       */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminCrawlerJobsById,
          { path: { id } },
          options,
        );
      },
    },

    /** Crawl schedule management — CRUD + enable/disable/trigger. */
    schedules: {
      /**
       * List crawl schedules across all workspaces visible to the admin actor.
       *
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to an array of {@link CrawlerSchedule} records.
       *
       * @example
       * ```typescript
       * const schedules = await admin.crawler.schedules.list();
       * ```
       */
      list: async (options?: RequestOptions): Promise<CrawlerSchedule[]> => {
        return rb.execute<CrawlerSchedule[]>(
          getAdminCrawlerSchedules,
          {},
          options,
        );
      },

      /**
       * Fetch a single crawl schedule by id.
       *
       * @param id - The schedule id.
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to the matching {@link CrawlerSchedule}.
       *
       * @example
       * ```typescript
       * const schedule = await admin.crawler.schedules.get('sched_abc');
       * ```
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<CrawlerSchedule> => {
        return rb.execute<CrawlerSchedule>(
          getAdminCrawlerSchedulesById,
          { path: { id } },
          options,
        );
      },

      /**
       * Create a new recurring crawl schedule. To pause/resume use
       * `enable()` / `disable()` — `enabled` is not part of the accept list.
       *
       * @param attributes - Schedule attributes; must include `workspace_id`,
       *   `name`, and `url`.
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to the newly created {@link CrawlerSchedule}.
       *
       * @example
       * ```typescript
       * const schedule = await admin.crawler.schedules.create({
       *   workspace_id: 'ws_abc',
       *   name: 'Daily docs',
       *   url: 'https://docs.example.com',
       *   frequency: 'daily',
       * });
       * ```
       */
      create: async (
        attributes: AdminCreateCrawlerScheduleAttributes,
        options?: RequestOptions,
      ): Promise<CrawlerSchedule> => {
        const bodyAttributes =
          buildAdminCreateCrawlerScheduleAttributes(attributes);

        return rb.execute<CrawlerSchedule>(
          postAdminCrawlerSchedules,
          {
            body: {
              data: { type: "crawler-schedule", attributes: bodyAttributes },
            },
          },
          options,
        );
      },

      /**
       * Update an existing crawl schedule (PATCH semantics).
       *
       * @param id - The schedule id.
       * @param attributes - Attributes to change.
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to the updated {@link CrawlerSchedule}.
       *
       * @example
       * ```typescript
       * await admin.crawler.schedules.update('csched_abc', {
       *   frequency: 'weekly',
       * });
       * ```
       */
      update: async (
        id: string,
        attributes: AdminUpdateCrawlerScheduleAttributes,
        options?: RequestOptions,
      ): Promise<CrawlerSchedule> => {
        const bodyAttributes =
          buildAdminUpdateCrawlerScheduleAttributes(attributes);

        return rb.execute<CrawlerSchedule>(
          patchAdminCrawlerSchedulesById,
          {
            path: { id },
            body: {
              data: {
                id,
                type: "crawler-schedule",
                attributes: bodyAttributes,
              },
            },
          },
          options,
        );
      },

      /**
       * Enable a paused crawl schedule. Sends a PATCH with no attributes.
       *
       * @param id - The schedule id.
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to the updated {@link CrawlerSchedule}.
       *
       * @example
       * ```typescript
       * await admin.crawler.schedules.enable('csched_abc');
       * ```
       */
      enable: async (
        id: string,
        options?: RequestOptions,
      ): Promise<CrawlerSchedule> => {
        return rb.execute<CrawlerSchedule>(
          patchAdminCrawlerSchedulesByIdEnable,
          { path: { id }, body: { data: { id, type: "crawler-schedule" } } },
          options,
        );
      },

      /**
       * Disable a crawl schedule (pause without deleting). Sends a PATCH
       * with no attributes.
       *
       * @param id - The schedule id.
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to the updated {@link CrawlerSchedule}.
       *
       * @example
       * ```typescript
       * await admin.crawler.schedules.disable('csched_abc');
       * ```
       */
      disable: async (
        id: string,
        options?: RequestOptions,
      ): Promise<CrawlerSchedule> => {
        return rb.execute<CrawlerSchedule>(
          patchAdminCrawlerSchedulesByIdDisable,
          { path: { id }, body: { data: { id, type: "crawler-schedule" } } },
          options,
        );
      },

      /**
       * Trigger a scheduled crawl immediately. Sends a PATCH with no
       * attributes; stamps `last_run_at` and enqueues a job.
       *
       * @param id - The schedule id.
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to the updated {@link CrawlerSchedule}.
       *
       * @example
       * ```typescript
       * await admin.crawler.schedules.trigger('csched_abc');
       * ```
       */
      trigger: async (
        id: string,
        options?: RequestOptions,
      ): Promise<CrawlerSchedule> => {
        return rb.execute<CrawlerSchedule>(
          patchAdminCrawlerSchedulesByIdTrigger,
          { path: { id }, body: { data: { id, type: "crawler-schedule" } } },
          options,
        );
      },

      /**
       * Permanently delete a crawl schedule.
       *
       * @param id - The schedule id.
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to `true` on successful deletion.
       *
       * @example
       * ```typescript
       * await admin.crawler.schedules.delete('csched_abc');
       * ```
       */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminCrawlerSchedulesById,
          { path: { id } },
          options,
        );
      },
    },

    /** Crawl result access — read extracted page content. */
    results: {
      /**
       * List crawl results across all jobs visible to the admin actor.
       *
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to an array of {@link CrawlerResult} records.
       *
       * @example
       * ```typescript
       * const results = await admin.crawler.results.list();
       * ```
       */
      list: async (options?: RequestOptions): Promise<CrawlerResult[]> => {
        return rb.execute<CrawlerResult[]>(getAdminCrawlerResults, {}, options);
      },

      /**
       * Fetch a single crawl result by id, including the extracted page
       * content (URL, title, Markdown body, metadata).
       *
       * @param id - The crawl result id.
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to the matching {@link CrawlerResult}.
       *
       * @example
       * ```typescript
       * const result = await admin.crawler.results.get('res_abc');
       * console.log(result.markdown);
       * ```
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<CrawlerResult> => {
        return rb.execute<CrawlerResult>(
          getAdminCrawlerResultsById,
          { path: { id } },
          options,
        );
      },
    },

    /** Site configuration — per-domain crawl settings (admin-only). */
    siteConfigs: {
      /**
       * List site configurations across all workspaces.
       *
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to an array of {@link CrawlerSiteConfig} records.
       */
      list: async (options?: RequestOptions): Promise<CrawlerSiteConfig[]> => {
        return rb.execute<CrawlerSiteConfig[]>(
          getAdminCrawlerSiteConfigs,
          {},
          options,
        );
      },

      /**
       * Fetch a single site configuration by id.
       *
       * @param id - The site config id.
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to the matching {@link CrawlerSiteConfig}.
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<CrawlerSiteConfig> => {
        return rb.execute<CrawlerSiteConfig>(
          getAdminCrawlerSiteConfigsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Create per-domain crawl settings (rate limit, strategy, custom
       * headers, robots.txt preference, browser-auth session binding).
       *
       * @param attributes - Site config attributes; must include
       *   `workspace_id` and `domain`.
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to the newly created {@link CrawlerSiteConfig}.
       *
       * @example
       * ```typescript
       * const config = await admin.crawler.siteConfigs.create({
       *   workspace_id: 'ws_abc',
       *   domain: 'docs.example.com',
       *   rate_limit_rpm: 30,
       *   preferred_strategy: 'auto',
       * });
       * ```
       */
      create: async (
        attributes: AdminCreateSiteConfigAttributes,
        options?: RequestOptions,
      ): Promise<CrawlerSiteConfig> => {
        const bodyAttributes = buildAdminCreateSiteConfigAttributes(attributes);

        return rb.execute<CrawlerSiteConfig>(
          postAdminCrawlerSiteConfigs,
          {
            body: {
              data: { type: "crawler-site-config", attributes: bodyAttributes },
            },
          },
          options,
        );
      },

      /**
       * Update an existing site configuration (PATCH semantics).
       *
       * @param id - The site config id.
       * @param attributes - Site config attributes to change.
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to the updated {@link CrawlerSiteConfig}.
       *
       * @example
       * ```typescript
       * await admin.crawler.siteConfigs.update('sc_abc', {
       *   rate_limit_rpm: 60,
       * });
       * ```
       */
      update: async (
        id: string,
        attributes: AdminUpdateSiteConfigAttributes,
        options?: RequestOptions,
      ): Promise<CrawlerSiteConfig> => {
        const bodyAttributes = buildAdminUpdateSiteConfigAttributes(attributes);

        return rb.execute<CrawlerSiteConfig>(
          patchAdminCrawlerSiteConfigsById,
          {
            path: { id },
            body: {
              data: {
                id,
                type: "crawler-site-config",
                attributes: bodyAttributes,
              },
            },
          },
          options,
        );
      },

      /**
       * Permanently delete a site configuration.
       *
       * @param id - The site config id.
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to `true` on successful deletion.
       *
       * @example
       * ```typescript
       * await admin.crawler.siteConfigs.delete('sc_abc');
       * ```
       */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminCrawlerSiteConfigsById,
          { path: { id } },
          options,
        );
      },
    },

    /**
     * News Monitors — workspace-scoped news monitoring configurations
     * (B11, `:news_monitoring` capability). Same surface as the client SDK
     * — Admin mounts the same routes under `/admin/crawler/news-monitors`.
     *
     * Mutating endpoints return HTTP 403 with code
     * `news_monitoring_capability_disabled` when the workspace's
     * Application lacks the capability.
     */
    newsMonitors: {
      /**
       * List NewsMonitors for the current workspace context, with offset
       * pagination support. Mirrors `admin.crawler.newsMonitors.list` on
       * the client SDK.
       *
       * @param options - Pagination (`page`, `pageSize`) + request options.
       * @returns A `Promise<NewsMonitor[]>` resolving to the monitors page.
       * @example
       * ```ts
       * const monitors = await admin.crawler.newsMonitors.list({ page: 1, pageSize: 25 });
       * ```
       */
      list: async (
        options?: { page?: number; pageSize?: number } & RequestOptions,
      ): Promise<NewsMonitor[]> => {
        return rb.execute<NewsMonitor[]>(
          getAdminCrawlerNewsMonitors,
          buildPageQuery(options?.page, options?.pageSize),
          options,
        );
      },

      /**
       * Get a NewsMonitor by id.
       *
       * @param id - NewsMonitor UUID.
       * @param options - Request options.
       * @returns A `Promise<NewsMonitor>`.
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<NewsMonitor> => {
        return rb.execute<NewsMonitor>(
          getAdminCrawlerNewsMonitorsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Create a NewsMonitor. Capability-gated on `:news_monitoring` —
       * returns HTTP 403 `news_monitoring_capability_disabled` when the
       * workspace's Application lacks the capability.
       *
       * @param attributes - Create attributes (typed; extra properties rejected server-side).
       * @param options - Request options.
       * @returns A `Promise<NewsMonitor>`.
       * @example
       * ```ts
       * const monitor = await admin.crawler.newsMonitors.create({
       *   name: "policy-monitor",
       *   topic: "policy updates",
       *   source_urls: ["https://example.com/news"],
       *   frequency: "daily",
       * });
       * ```
       */
      create: async (
        attributes: CreateNewsMonitorAttributes,
        options?: RequestOptions,
      ): Promise<NewsMonitor> => {
        const bodyAttributes = buildCreateNewsMonitorAttributes(attributes);

        return rb.execute<NewsMonitor>(
          postAdminCrawlerNewsMonitors,
          {
            body: {
              data: { type: "news-monitor", attributes: bodyAttributes },
            },
          },
          options,
        );
      },

      /**
       * Update a NewsMonitor (PATCH). Capability-gated on
       * `:news_monitoring`.
       *
       * @param id - NewsMonitor UUID.
       * @param attributes - Update attributes (PATCH semantics — only
       *   provided fields are modified).
       * @param options - Request options.
       * @returns A `Promise<NewsMonitor>`.
       * @example
       * ```ts
       * await admin.crawler.newsMonitors.update(id, { topic: "new topic" });
       * ```
       */
      update: async (
        id: string,
        attributes: UpdateNewsMonitorAttributes,
        options?: RequestOptions,
      ): Promise<NewsMonitor> => {
        const bodyAttributes = buildUpdateNewsMonitorAttributes(attributes);

        return rb.execute<NewsMonitor>(
          patchAdminCrawlerNewsMonitorsById,
          {
            path: { id },
            body: {
              data: { id, type: "news-monitor", attributes: bodyAttributes },
            },
          },
          options,
        );
      },

      /**
       * Pause a NewsMonitor — sets status to `:paused`. The scheduler
       * skips paused monitors.
       *
       * @param id - NewsMonitor UUID.
       * @param options - Request options.
       * @returns A `Promise<NewsMonitor>` with `status: "paused"`.
       * @example
       * ```ts
       * await admin.crawler.newsMonitors.pause(monitorId);
       * ```
       */
      pause: async (
        id: string,
        options?: RequestOptions,
      ): Promise<NewsMonitor> => {
        return rb.execute<NewsMonitor>(
          patchAdminCrawlerNewsMonitorsByIdPause,
          { path: { id }, body: { data: { id, type: "news-monitor" } } },
          options,
        );
      },

      /**
       * Resume a paused NewsMonitor — sets status back to `:active`.
       *
       * @param id - NewsMonitor UUID.
       * @param options - Request options.
       * @returns A `Promise<NewsMonitor>` with `status: "active"`.
       * @example
       * ```ts
       * await admin.crawler.newsMonitors.resume(monitorId);
       * ```
       */
      resume: async (
        id: string,
        options?: RequestOptions,
      ): Promise<NewsMonitor> => {
        return rb.execute<NewsMonitor>(
          patchAdminCrawlerNewsMonitorsByIdResume,
          { path: { id }, body: { data: { id, type: "news-monitor" } } },
          options,
        );
      },

      /**
       * Archive a NewsMonitor — sets status to `:archived` (terminal).
       * Archived monitors are excluded from scheduler sweeps.
       *
       * @param id - NewsMonitor UUID.
       * @param options - Request options.
       * @returns A `Promise<NewsMonitor>` with `status: "archived"`.
       *
       * @example
       * ```ts
       * await admin.crawler.newsMonitors.archive(monitorId);
       * ```
       */
      archive: async (
        id: string,
        options?: RequestOptions,
      ): Promise<NewsMonitor> => {
        return rb.execute<NewsMonitor>(
          patchAdminCrawlerNewsMonitorsByIdArchive,
          { path: { id }, body: { data: { id, type: "news-monitor" } } },
          options,
        );
      },

      /**
       * Permanently delete a NewsMonitor.
       *
       * @param id - NewsMonitor UUID.
       * @param options - Request options.
       * @returns A `Promise<true>` on success.
       *
       * @example
       * ```ts
       * await admin.crawler.newsMonitors.delete(monitorId);
       * ```
       */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminCrawlerNewsMonitorsById,
          { path: { id } },
          options,
        );
      },
    },

    /**
     * News Summaries — read-only access to AI-generated summaries produced
     * by NewsMonitor runs.
     */
    newsSummaries: {
      /**
       * List NewsSummaries for the current workspace context, with offset
       * pagination support. Mirrors the client SDK list shape.
       *
       * @param options - Pagination (`page`, `pageSize`) + request options.
       * @returns A `Promise<NewsSummary[]>`.
       *
       * @example
       * ```typescript
       * const summaries = await admin.crawler.newsSummaries.list({ pageSize: 50 });
       * ```
       */
      list: async (
        options?: { page?: number; pageSize?: number } & RequestOptions,
      ): Promise<NewsSummary[]> => {
        return rb.execute<NewsSummary[]>(
          getAdminCrawlerNewsSummaries,
          buildPageQuery(options?.page, options?.pageSize),
          options,
        );
      },

      /**
       * Get a NewsSummary by id.
       *
       * @param id - NewsSummary UUID.
       * @param options - Request options.
       * @returns A `Promise<NewsSummary>`.
       *
       * @example
       * ```typescript
       * const summary = await admin.crawler.newsSummaries.get('sum_abc');
       * ```
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<NewsSummary> => {
        return rb.execute<NewsSummary>(
          getAdminCrawlerNewsSummariesById,
          { path: { id } },
          options,
        );
      },
    },

    /**
     * Browser Sessions — encrypted, per-domain authenticated session
     * state used by `:browser_session` strategy crawls. `session_state`
     * is encrypted at rest via `EncryptSessionState` and is NEVER
     * returned in API responses (marked sensitive on the resource).
     *
     * ISVs configure these to support authenticated crawling without
     * repeating the login flow on every scheduled run.
     */
    browserSessions: {
      /**
       * List browser sessions across all workspaces visible to the admin
       * actor.
       *
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to an array of {@link BrowserSession} records.
       */
      list: async (options?: RequestOptions): Promise<BrowserSession[]> => {
        return rb.execute<BrowserSession[]>(
          getAdminCrawlerBrowserSessions,
          {},
          options,
        );
      },

      /**
       * Fetch a single browser session by id.
       *
       * @param id - The browser session id.
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to the matching {@link BrowserSession}.
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<BrowserSession> => {
        return rb.execute<BrowserSession>(
          getAdminCrawlerBrowserSessionsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Create a new browser session for a domain. `session_state` is
       * encrypted via the `EncryptSessionState` change before persistence.
       *
       * @param attributes - Session attributes; must include `workspace_id`,
       *   `domain`, and `session_state`.
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to the newly created {@link BrowserSession}.
       *
       * @example
       * ```typescript
       * const session = await admin.crawler.browserSessions.create({
       *   workspace_id: 'ws_abc',
       *   domain: 'example.com',
       *   session_state: JSON.stringify({ cookies: [...] }),
       *   expires_at: '2026-06-01T00:00:00Z',
       * });
       * ```
       */
      create: async (
        attributes: AdminCreateBrowserSessionAttributes,
        options?: RequestOptions,
      ): Promise<BrowserSession> => {
        const bodyAttributes =
          buildAdminCreateBrowserSessionAttributes(attributes);

        return rb.execute<BrowserSession>(
          postAdminCrawlerBrowserSessions,
          {
            body: {
              data: { type: "browser-session", attributes: bodyAttributes },
            },
          },
          options,
        );
      },

      /**
       * Refresh a browser session by rotating `session_state`. Re-encrypts
       * the new state and stamps `last_used_at`. Only `session_state` may
       * be modified via this endpoint.
       *
       * @param id - The browser session id.
       * @param attributes - Refresh attributes — only `session_state`.
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to the refreshed {@link BrowserSession}.
       *
       * @example
       * ```typescript
       * await admin.crawler.browserSessions.refresh(sessionId, {
       *   session_state: JSON.stringify({ cookies: [...] }),
       * });
       * ```
       */
      refresh: async (
        id: string,
        attributes: AdminRefreshBrowserSessionAttributes,
        options?: RequestOptions,
      ): Promise<BrowserSession> => {
        const bodyAttributes =
          buildAdminRefreshBrowserSessionAttributes(attributes);

        return rb.execute<BrowserSession>(
          patchAdminCrawlerBrowserSessionsByIdRefresh,
          {
            path: { id },
            body: {
              data: {
                id,
                type: "browser-session",
                attributes: bodyAttributes,
              },
            },
          },
          options,
        );
      },

      /**
       * Permanently delete a browser session.
       *
       * @param id - The browser session id.
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to `true` on successful deletion.
       *
       * @example
       * ```typescript
       * await admin.crawler.browserSessions.delete(sessionId);
       * ```
       */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminCrawlerBrowserSessionsById,
          { path: { id } },
          options,
        );
      },
    },
  };
}

export type AdminCrawlerAPI = ReturnType<typeof createCrawlerNamespace>;
